import {
    Create,
    Form,
    Image,
    Input,
    InputNumber,
    Select,
    Switch,
    Table,
    TagField,
    TextField,
    Upload,
    UploadFile,
    UploadProps,
    useForm,
    useSelect,
    useTable,
} from '@pankod/refine-antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import { useMemo, useState } from 'react';
import 'react-mde/lib/styles/css/react-mde-all.css';
import { ILabel } from 'types/label.interface';
import { IUser, IUserResponse } from 'types/user.interface';
import { beforeUpload } from 'utils';
import './styles.scss';
import useMediaScreen from 'hooks/useMediaScreen';

export const LabelCreate: React.FC<IResourceComponentsProps> = () => {
    const [fileListSquare, setFileListSquare] = useState<UploadFile[]>();
    const [fileListBanner, setFileListBanner] = useState<UploadFile[]>();
    const [contributorPricing, setContributorPricing] = useState<boolean>(false);
    const [minPrice, setMinPrice] = useState<number>(0);
    const [maxPrice, setMaxPrice] = useState<number>(500);
    const [minUpload, setMinUpload] = useState<number>(0);
    const [maxUpload, setMaxUpload] = useState<number>(500);
    const [userSelect, setUserSelect] = useState<string[]>([]);
    const { isMobile } = useMediaScreen();

    const { formProps, saveButtonProps, formLoading } = useForm<ILabel>();

    const { selectProps: UserSelectProps } = useSelect<IUser>({
        resource: 'users/contributor',
        fetchSize: 100,
        optionLabel: 'username',
        optionValue: '_id',
    });

    const { tableProps, searchFormProps } = useTable<IUserResponse>({
        resource: `users/contributor`,
        initialPageSize: 100,
    });

    const contributorProp = useMemo(() => {
        let userTable = (tableProps?.dataSource || []).filter((user: any) => userSelect.includes(user._id));
        return {
            dataSource: userTable,
        };
    }, [userSelect, tableProps]);

    const handleChangeSquare: UploadProps['onChange'] = ({ fileList: newFileList }) => setFileListSquare(newFileList);

    const handleChangeBanner: UploadProps['onChange'] = ({ fileList: newFileList }) => setFileListBanner(newFileList);

    const handleChangeContributorPricing = () => {
        setContributorPricing((pre) => !pre);
    };

    return (
        <Create saveButtonProps={saveButtonProps} isLoading={formLoading}>
            <div style={{ display: 'flex', height: '100%' }}>
                <Form {...formProps} layout='vertical' style={{ width: '100%' }}>
                    <Form.Item
                        label='Name'
                        name='name'
                        rules={[
                            {
                                required: true,
                                message: 'Name is required',
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label='Biography'
                        name='biography'
                        rules={[
                            {
                                required: true,
                                message: 'Biography is required',
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <div className='edit-title' style={{flexDirection: isMobile ? "column" : "row"}}>
                        <Form.Item
                            className='edit-item w48'
                            label='Upload Min'
                            name='minUpload'
                            rules={[
                                {
                                    required: true,
                                    message: 'Upload Min is required',
                                },
                                {
                                    min: 0,
                                    type: 'number',
                                    message: 'Min Upload must be more than 0',
                                },
                                {
                                    max: maxUpload - 1,
                                    type: 'number',
                                    message: 'Min Upload must be less than Max Upload',
                                },
                            ]}
                        >
                            <InputNumber onChange={(value: number) => setMinUpload(value)} />
                        </Form.Item>
                        <Form.Item
                            className='edit-item w48'
                            label='Upload Max'
                            name='maxUpload'
                            rules={[
                                {
                                    required: true,
                                    message: 'Upload Max is required',
                                },
                                {
                                    min: 0,
                                    type: 'number',
                                    message: 'Max Upload must be more than 0',
                                },
                                {
                                    min: minUpload + 1,
                                    type: 'number',
                                    message: 'Max Upload must be more than Min Upload',
                                },
                            ]}
                        >
                            <InputNumber onChange={(value: number) => setMaxUpload(value)} />
                        </Form.Item>
                    </div>
                    <div className='edit-title' style={{flexDirection: isMobile ? "column" : "row"}}>
                        <Form.Item className='edit-item w48' label='Default Split (%)' name='percentageOfTokensCanKeep'>
                            <InputNumber min={0} max={100} step={1} />
                        </Form.Item>
                        <Form.Item
                            className='edit-item w48'
                            label='Download Attempts On Purchases'
                            name='numberDownloads'
                            rules={[
                                {
                                    required: true,
                                    message: 'Download Attempts On Purchases is required',
                                },
                            ]}
                        >
                            <InputNumber />
                        </Form.Item>
                    </div>
                    <div className='edit-title' style={{flexDirection: isMobile ? "column" : "row"}}>
                        <Form.Item
                            className='edit-item w48'
                            label='Is Contributor Pricing'
                            name='isContributorPricing'
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Switch defaultChecked={false} onChange={handleChangeContributorPricing} />
                        </Form.Item>
                        <Form.Item
                            className='edit-item w48'
                            label='IsActive'
                            name='isActive'
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Switch defaultChecked={true} />
                        </Form.Item>
                        <Form.Item
                            className='edit-item w48'
                            label='Is Default'
                            name='isDefault'
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Switch defaultChecked={false} />
                        </Form.Item>
                    </div>
                    <div>
                        {!contributorPricing ? (
                            <div>
                                <Form.Item
                                    className='edit-item w48'
                                    label='Default Track Price'
                                    name='defaultTrackPrice'
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Default Track Price is required',
                                        },
                                    ]}
                                >
                                    <InputNumber />
                                </Form.Item>
                            </div>
                        ) : (
                            <div>
                                <div className='edit-title'>
                                    <Form.Item
                                        className='edit-item w48'
                                        label='Min Price'
                                        name='minPrice'
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Min Price is required',
                                            },
                                            {
                                                min: 0,
                                                type: 'number',
                                                message: 'Min Price must be more than 0',
                                            },
                                            {
                                                max: maxPrice - 1,
                                                type: 'number',
                                                message: 'Min Price must be less than Max Price',
                                            },
                                        ]}
                                    >
                                        <InputNumber onChange={(value: number) => setMinPrice(value)} />
                                    </Form.Item>
                                    <Form.Item
                                        className='edit-item w48'
                                        label='Max Price'
                                        name='maxPrice'
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Max Price is required',
                                            },
                                            {
                                                min: 0,
                                                type: 'number',
                                                message: 'Max Price must be more than 0',
                                            },
                                            {
                                                min: minPrice + 1,
                                                type: 'number',
                                                message: 'Max Price must be more than Min Price',
                                            },
                                        ]}
                                    >
                                        <InputNumber onChange={(value: number) => setMaxPrice(value)} />
                                    </Form.Item>
                                </div>
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: isMobile ? "column" : "row" }}>
                        <Form.Item
                            className='displayBlock w48'
                            label='Square Image'
                            name='squareImageUrl'
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Upload
                                accept='image/*'
                                name='squareImageUrl'
                                listType='picture-card'
                                fileList={fileListSquare}
                                beforeUpload={beforeUpload}
                                onChange={handleChangeSquare}
                                maxCount={1}
                            >
                                change
                            </Upload>
                        </Form.Item>
                        <Form.Item
                            className='displayBlock w48'
                            label='Banner Image'
                            name='bannerImageUrl'
                            rules={[
                                {
                                    required: false,
                                },
                            ]}
                        >
                            <Upload
                                accept='image/*'
                                name='bannerImageUrl'
                                listType='picture-card'
                                fileList={fileListBanner}
                                beforeUpload={beforeUpload}
                                onChange={handleChangeBanner}
                                maxCount={1}
                            >
                                change
                            </Upload>
                        </Form.Item>
                    </div>
                    <Form.Item
                        className='edit-item w48'
                        label='Label bg color'
                        name='labelBgColor'
                        rules={[
                            {
                                required: false,
                            },
                        ]}
                    >
                        <Input style={{ width: '100px' }} />
                    </Form.Item>
                    <Form.Item
                        label='Contributors'
                        name='contributors'
                        rules={[
                            {
                                required: false,
                                message: 'Contributors is required',
                            },
                        ]}
                    >
                        <Select
                            mode='multiple'
                            {...UserSelectProps}
                            onChange={(value: any) => {
                                searchFormProps?.form?.setFields(value);
                                searchFormProps?.form?.submit();
                                setUserSelect(value);
                            }}
                        />
                    </Form.Item>
                </Form>
            </div>

            <Table {...contributorProp} rowKey='_id' style={{overflow: 'scroll'}}>
                <Table.Column dataIndex='avatar' key='avatar' title='Avatar' render={(value) => <Image width={50} src={value} />} />
                <Table.Column dataIndex='username' key='username' title='Username' render={(value) => <TextField value={value} />} />
                <Table.Column dataIndex='email' key='email' title='Email' render={(value) => <TagField value={value} />} />
                <Table.Column dataIndex='purchasedTokensRemaining' key='purchasedTokensRemaining' title={'Purchased'} render={(value) => <TextField value={value} />} />
                <Table.Column dataIndex='starsRemaining' key='starsRemaining' title='Stars' render={(value) => <TextField value={value} />} />
            </Table>
        </Create>
    );
};
